import { useEffect, useRef } from "react";
import { openTelemetryStream } from "../../lib/telemetryStreamClient";
import { useTelemetryConnectionStore } from "../../stores/useTelemetryConnectionStore";

/**
 * Owns a single facility's telemetry SSE connection lifecycle. Mounted
 * exactly once, inside FacilityLayout — never inside an individual page
 * component (§02.1 of the FE push architecture spec) — so switching pages
 * within the same facility never touches the connection, only switching
 * facilities does.
 *
 * State machine (§03): Idle → Connecting → Live → Reconnecting →
 * Disconnected, plus Suspended for backgrounded tabs. There is no
 * "Degraded/fallback-polling" state — a sustained failure escalates to a
 * visible "Disconnected" and keeps retrying indefinitely, it never falls
 * back to `refetchInterval`-style polling (§01's explicit non-goal).
 *
 * SCOPING NOTE: the transport (telemetryStreamClient.ts) only reports two
 * states it actually knows about — "live" and "reconnecting". This hook is
 * what turns "reconnecting" into either a quiet "reconnecting" or an
 * escalated "disconnected", and is what tab-visibility "suspended" comes
 * from — the transport has no visibility awareness of its own.
 */

const VISIBILITY_GRACE_MS = 90_000;
const DISCONNECT_ATTEMPT_THRESHOLD = 3;
const DISCONNECT_DURATION_THRESHOLD_MS = 15_000;

export interface UseTelemetryStreamOptions {
  /** Called for every parsed SSE record forwarded from the transport
   *  (e.g. "telemetry", "stale", "bye"). Patch/invalidation logic lives
   *  in the caller, not here — this hook only owns the connection. */
  onEvent?: (eventType: string, data: unknown) => void;
  /** Called every time the connection reaches "live" — on the very first
   *  connect and again after every reconnect (including one triggered by
   *  regaining tab visibility). Callers use this to re-fetch REST state so
   *  a push-triggered UI never shows data older than what's now live. */
  onConnected?: () => void;
  /** When false, no connection is opened (and any open one is torn down).
   *  Defaults to true. FacilityLayout passes false while a Joules platform
   *  admin views a deactivated facility (JAA-1117): there's no live data to
   *  receive, and an idle connection would misleadingly show "Live". */
  enabled?: boolean;
}

export function useTelemetryStream(
  facilityId: number,
  options: UseTelemetryStreamOptions = {},
): void {
  // Refs so a caller passing a new inline function every render doesn't
  // force the connection effect to tear down and reconnect unnecessarily —
  // only facilityId should ever do that. Synced via an effect (not during
  // render) since mutating ref.current during render breaks React's rules.
  const onEventRef = useRef(options.onEvent);
  const onConnectedRef = useRef(options.onConnected);
  useEffect(() => {
    onEventRef.current = options.onEvent;
    onConnectedRef.current = options.onConnected;
  });

  const enabled = options.enabled ?? true;

  useEffect(() => {
    if (!enabled) return;

    let teardownStream: (() => void) | null = null;
    let visibilityTimer: ReturnType<typeof setTimeout> | undefined;
    let suspended = false;
    // Terminal: the facility was deactivated (event: bye, reason
    // "deactivated") or the initial connect got a 403 (deactivated or
    // otherwise forbidden). Once set, nothing should reconnect this
    // connection again — retrying can never resolve either case.
    let terminal = false;
    // Tracks when the current unexpected-reconnect streak started, so a
    // sustained failure can escalate on elapsed time as well as attempt
    // count (§03: "past ~3 failed attempts or ~15s of continuous retrying").
    let reconnectingSince: number | null = null;

    const disconnect = () => {
      teardownStream?.();
      teardownStream = null;
    };

    const connect = () => {
      useTelemetryConnectionStore.getState().setConnectionState(facilityId, "connecting");

      teardownStream = openTelemetryStream({
        url: `/api/v1/facilities/${facilityId}/telemetry/stream`,
        onEvent: (eventType, data) => {
          useTelemetryConnectionStore.getState().recordEvent(facilityId);
          onEventRef.current?.(eventType, data);

          if (eventType === "bye" && (data as { reason?: string } | undefined)?.reason === "deactivated") {
            terminal = true;
            disconnect();
            useTelemetryConnectionStore.getState().setConnectionState(facilityId, "deactivated");
          }
        },
        onStateChange: (state, meta) => {
          if (terminal) return; // never revert out of a terminal state

          if (state === "forbidden") {
            terminal = true;
            disconnect();
            useTelemetryConnectionStore
              .getState()
              .setConnectionState(facilityId, meta?.deactivated ? "deactivated" : "forbidden");
            return;
          }

          if (state === "not_found") {
            terminal = true;
            disconnect();
            useTelemetryConnectionStore.getState().setConnectionState(facilityId, "not_found");
            return;
          }

          if (state === "live") {
            reconnectingSince = null;
            useTelemetryConnectionStore.getState().resetAttempts(facilityId);
            useTelemetryConnectionStore.getState().setConnectionState(facilityId, "live");
            onConnectedRef.current?.();
            return;
          }

          // state === "reconnecting"
          if (meta?.expected) {
            // Scheduled close (event: bye) — reconnect silently, no UI change (§4.3).
            return;
          }

          useTelemetryConnectionStore.getState().incrementAttempt(facilityId);
          if (reconnectingSince === null) reconnectingSince = Date.now();

          const attempts =
            useTelemetryConnectionStore.getState().connections[facilityId]?.attemptCount ?? 0;
          const elapsed = Date.now() - reconnectingSince;
          const shouldEscalate =
            attempts >= DISCONNECT_ATTEMPT_THRESHOLD || elapsed >= DISCONNECT_DURATION_THRESHOLD_MS;

          useTelemetryConnectionStore
            .getState()
            .setConnectionState(facilityId, shouldEscalate ? "disconnected" : "reconnecting");
        },
      });
    };

    const handleVisibilityChange = () => {
      if (terminal) return; // no suspend/resume cycling once terminal

      if (document.hidden) {
        if (visibilityTimer) return; // already counting down
        visibilityTimer = setTimeout(() => {
          suspended = true;
          disconnect();
          useTelemetryConnectionStore.getState().setConnectionState(facilityId, "suspended");
        }, VISIBILITY_GRACE_MS);
      } else {
        if (visibilityTimer) {
          clearTimeout(visibilityTimer);
          visibilityTimer = undefined;
        }
        if (suspended) {
          suspended = false;
          reconnectingSince = null;
          connect();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    connect();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (visibilityTimer) clearTimeout(visibilityTimer);
      disconnect();
      useTelemetryConnectionStore.getState().clearConnection(facilityId);
    };
    // Keyed on facilityId (and enabled) alone — onEvent/onConnected are read
    // through refs above so a fresh callback identity never triggers a
    // reconnect. Only switching facilities, or enabling/disabling the
    // stream, should tear down and reopen.
  }, [facilityId, enabled]);
}
